import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from 'react-router';
import { Link, useSearchParams } from 'react-router-dom';
import { audiencesApi } from '../../api/audiences';
import { userApi } from '../../api/user';
import Inactive from '../../assets/icons/addresses.svg?url';
import Chart from '../../assets/images/asset/chart.png';
import Cal from '../../assets/images/asset/Icon.png';
import Owned from '../../assets/images/asset/member.png';
import Matched from '../../assets/images/asset/wallets_matched.svg?url';
import EditableTitle from '../../components/base/EditableTitle';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/base/Notifications';
import Title from '../../components/base/Title';
import EditableFoldersList from '../../components/ui/EditableFoldersList';
import { LoadingLines } from '../../components/ui/modals/SearchModal/LoadingList';
import Reach from '../../components/ui/Reach';
import WidgetCard from '../../components/ui/WidgetCard';
import uniqueId from '../../utils/uniqueId';
import { getCardValue, getEthPrice } from '../../utils/values';
import styles from './CustomListDetails.module.scss';
import DappUsage from './DappUsagge';
import DevActivity from './DevActivity';
import Exchanges from './Exchanges';
import InvestorPortfolio from './InvestorPortfolio';
import InvestorProfile from './InvestorProfile';
import List from './List';

const DEFAULT_TAB = 'Audience Profile';

const CustomListDetails = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const navType = useNavigationType();
  const [searchParams, setSearchParams] = useSearchParams();
  const [itemPage, setItemPage] = useState(
    searchParams.get('tab')?.replace('+', ' ') || DEFAULT_TAB
  );
  const [selectedTab, setSelectedTab] = useState(null);
  const [pages, setPages] = useState([]);
  const [widgetData, setWidgetData] = useState([{}, {}, {}, {}, {}]);

  const { id } = useParams();

  const [titleValue, setTitleValue] = useState('');

  const getPage = useCallback(
    (pageLabel) => pages.find((page) => page.label === pageLabel),
    [pages]
  );

  useEffect(() => {
    const tabName = searchParams.get('tab')?.replace('+', ' ') || DEFAULT_TAB;
    setSelectedTab(pages.length ? getPage(tabName) : null);
  }, [searchParams, pages]);

  const {
    data: customListName,
    isLoading: isCustomListNameLoading,
    isFetching: isCustomListNameFetching,
  } = audiencesApi.useGetCustomListNameByIdQuery({ id });

  const {
    data: customList,
    isLoading: isCustomListLoading,
    isFetching: isCustomListFetching,
  } = audiencesApi.useGetCustomListAnalyticsQuery({ id, order: 'ASC' });

  const {
    data: reachData,
    isLoading: isReachDataLoading,
    isFetching: isReachDataFetching,
  } = audiencesApi.useCustomListReachQuery(id);

  const {
    data: insightData,
    isLoading: isInsightDataLoading,
    isFetching: isInsightDataFetching,
  } = audiencesApi.useGetCustomListInsightQuery(id);

  const [updateCustomList, updateCustomListResult] =
    audiencesApi.useUpdateCustomListMutation();

  const { refetch } = userApi.useGetAssetsQuery();

  useEffect(() => {
    if (
      customListName &&
      !(isCustomListNameLoading || isCustomListNameFetching)
    ) {
      setTitleValue(customListName);
    }
  }, [customListName, isCustomListNameLoading, isCustomListNameFetching]);

  useEffect(() => {
    if (customList) {
      const ListCount =
        (customList?.member_count || 0) -
        (customList?.inactive_address_count || 0);
      setPages([
        {
          label: 'Audience Profile',
          component: (
            <InvestorProfile
              holders={customList?.member_count}
              isHoldersLoading={isCustomListLoading || isCustomListFetching}
              audienceProfileData={insightData}
              audienceProfileIsLoading={
                isInsightDataLoading || isInsightDataFetching
              }
            />
          ),
        },
        {
          label: 'Audience Portfolio',
          component: (
            <InvestorPortfolio
              holders={insightData?.wallet_count}
              assetsRefetch={refetch}
            />
          ),
        },
        {
          label: 'List',
          component: <List walletCounts={insightData?.wallet_count} />,
        },
        {
          label: 'Dapp Usage',
          component: <DappUsage holders={insightData?.wallet_count} />,
        },
        {
          label: 'Centralized Exchanges',
          component: <Exchanges holders={insightData?.wallet_count} />,
        },
        {
          label: 'Developer activity',
          component: <DevActivity holders={insightData?.wallet_count} />,
        },
      ]);

      setWidgetData([
        {
          title: 'Profile Count',
          value: getCardValue(customList.member_count),
          icon: Owned,
        },
        {
          title: 'Wallets Matched',
          value: getCardValue(customList.wallets_matched),
          icon: Matched,
          info: 'Profiles that have wallet addresses associated with them',
        },
        {
          title: 'Inactive Wallets',
          value: customList.inactive_address_count
            ? getCardValue(customList.inactive_address_count)
            : 0,
          icon: Inactive,
          info: 'Addresses without on-chain activity',
        },
        {
          title: 'Cumulated Portfolio Value',
          value: getEthPrice(customList.cumulated_portfolio_value),
          icon: Chart,
        },
        {
          title: 'Median Portfolio Value',
          value: getEthPrice(
            customList.cumulated_portfolio_value
              ? customList.median_portfolio_value
              : 0
          ),
          icon: Cal,
        },
      ]);
    }
  }, [
    customList,
    insightData,
    isInsightDataLoading,
    isInsightDataFetching,
    refetch,
  ]);

  useEffect(() => {
    setItemPage(
      searchParams.get('tab')?.replace('+', ' ') || 'Audience Profile'
    );
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('scroll') && navType === 'POP') {
      setTimeout(
        () =>
          window.scrollTo({
            top: +searchParams.get('scroll'),
            left: 0,
            behavior: 'smooth',
          }),
        500
      );
    }
  }, [navType, searchParams]);

  useEffect(() => {
    if (updateCustomListResult.isSuccess) {
      showSuccessMessage('Title name was saved');
      updateCustomListResult.reset();
    }
    if (updateCustomListResult.isError) {
      showErrorMessage(
        updateCustomListResult?.error?.data?.name[0] || 'Something went wrong'
      );
      updateCustomListResult.reset();
    }
  }, [updateCustomListResult]);

  const useInFlow = () => {
    if (!customList) return;

    const listConfig = {
      config_name: 'List',
      frequency: '',
      list_id: id,
      list_name: customList.custom_list_name,
      user_input: false,
      population: customList.member_count,
      name: customList.custom_list_name,
      node_id: 'node_0',
    };

    navigate('/add-flow', { state: { config: [listConfig] } });
  };

  const fetchRender = useMemo(
    () => (
      <>
        <div className="asset-section">
          <div className="mt-4 mx-4 pt-3 pb-2">
            <Title>Portfolio value</Title>
          </div>
          <div className="dropdown-divider w-100" />
          <div className={`${styles.wrapper} d-gap-3 d-flex flex-column`}>
            <div className="d-flex gap-3 flex-column flex-sm-row pb-4">
              {widgetData.map((item) => (
                <WidgetCard
                  key={uniqueId('custom-list-widget')}
                  icon={item.icon}
                  info={item.info}
                  growth={item.growth}
                  title={item.title}
                  value={item.value}
                  isLoading={
                    isCustomListLoading ||
                    isCustomListFetching ||
                    item.isLoading
                  }
                />
              ))}
            </div>
            <Reach
              data={reachData}
              isLoading={
                isReachDataFetching ||
                isReachDataLoading ||
                isCustomListLoading ||
                isCustomListFetching
              }
              totalCount={customList?.member_count}
            />
          </div>
        </div>
        <div className="asset-section m-o mt-4">
          {isCustomListLoading || isCustomListFetching ? (
            <div className="mt-2">
              <LoadingLines />
            </div>
          ) : (
            <>
              <div className="title-gap asset-navigation">
                <ul className="nav nav-pills gap-2">
                  {pages.map((elem) => (
                    <li
                      role="presentation"
                      className="nav-item"
                      key={uniqueId('custom-tab')}
                      onClick={() => {
                        setItemPage(elem.label);
                        setSelectedTab(getPage(elem.label));
                        setSearchParams({
                          tab: elem.label,
                          scroll: '',
                        });
                      }}
                    >
                      <Link
                        className={`nav-link ${
                          elem.label === itemPage ? 'active' : ''
                        }`}
                        to={`${pathname}${search}`}
                      >
                        {elem.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="dropdown-divider w-100" />
              <div className="">{selectedTab?.component}</div>
            </>
          )}
        </div>
      </>
    ),
    [
      customList?.custom_list_name,
      customList?.member_count,
      isCustomListFetching,
      isCustomListLoading,
      isReachDataFetching,
      isReachDataLoading,
      itemPage,
      pages,
      pathname,
      reachData,
      search,
      setSearchParams,
      widgetData,
      navType,
      insightData,
      isInsightDataLoading,
      isInsightDataFetching,
      selectedTab,
    ]
  );

  return (
    <>
      {!(isCustomListLoading || isCustomListFetching) && (
        <Helmet>
          <meta charSet="utf-8" />
          <title>{titleValue} Audiences - Absolute Labs Platform</title>
        </Helmet>
      )}

      <div className={styles.title_wrapper}>
        <div>
          <div className="d-flex gap-2 align-items-center">
            {!titleValue ||
            isCustomListNameLoading ||
            isCustomListNameFetching ? (
              <LoadingLines />
            ) : (
              <EditableTitle
                defaultTitle="Add the list name"
                titleValue={titleValue}
                setTitleValue={setTitleValue}
                onSave={() =>
                  updateCustomList({ id, body: { name: titleValue } })
                }
                edit={false}
                triggerNotification={false}
              />
            )}
          </div>
          <div className="mt-2">
            <EditableFoldersList itemId={id} itemType="custom_lists" />
          </div>
        </div>
        <div className={styles.actions_wrapper}>
          <button
            type="button"
            className="outline-blue-button"
            onClick={() => navigate(`/add-custom-list/${id}`)}
          >
            Edit
          </button>
          <button
            type="button"
            className="regular-button text-nowrap"
            onClick={useInFlow}
          >
            Use in Flow
          </button>
        </div>
      </div>

      {fetchRender}
    </>
  );
};

export default CustomListDetails;
